import type { TFunction } from 'i18next'
import isValidEmail from '@/shared/tools/isValidEmail'
import { departureCountries } from '../../AdditionalSales/sales/LeadMovingNextoriesSale/Config'
import type { ChoiceOptionItem } from './blocks/ChoiceChips'
import type { RecapRow } from './blocks/ClientRecap'
import { isNotPast, parseFrenchDate } from './blocks/masks'

export interface NextoriesData {
  departureCity?: string | null
  departureZipcode?: string | null
  departureCountry?: string | null
  earliestMovingDate?: string | null
  earliestMovingDateInput?: string | null
  movingDescription?: string | null
}

export interface NextoriesAddress {
  streetNumber?: string | null
  streetName?: string | null
  zipcode?: string | null
  city?: string | null
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/

export const countryOptions: ChoiceOptionItem[] = departureCountries
  .filter(country => country.id !== '')
  .map(country => ({ value: country.id, label: country.label }))

export const isDepartureCityValid = (value: string) => value.trim().length > 1

export const isDepartureZipcodeValid = (value: string) => /^\d{4,5}$/.test(value.trim())

export const isMovingDateValid = (value: string) => isNotPast(parseFrenchDate(value))

const pad = (value: number) => String(value).padStart(2, '0')

export const movingDateIso = (text: string) => {
  const date = parseFrenchDate(text)
  if (!date || !isNotPast(date)) return ''

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export const movingDateText = (data: NextoriesData) => {
  if (typeof data.earliestMovingDateInput === 'string') return data.earliestMovingDateInput
  const match = ISO_DATE.exec(data.earliestMovingDate ?? '')

  return match ? `${match[3]}/${match[2]}/${match[1]}` : ''
}

export const movingDatePatch = (text: string): Partial<NextoriesData> => ({
  earliestMovingDateInput: text,
  earliestMovingDate: movingDateIso(text),
})

export const hasMovingAnswer = (data: NextoriesData) =>
  [
    data.departureCity,
    data.departureZipcode,
    data.departureCountry,
    data.earliestMovingDate,
    data.earliestMovingDateInput,
    data.movingDescription,
  ].some(value => (value ?? '').trim() !== '')

export const addressLabel = (address: NextoriesAddress) =>
  [address.streetNumber, address.streetName, address.zipcode, address.city].filter(Boolean).join(' ')

interface RecapValues {
  civility?: string | null
  email?: string | null
  addressSelected: boolean
  address: NextoriesAddress
}

export const nextoriesRecap = (
  t: TFunction,
  { civility, email, addressSelected, address }: RecapValues,
): RecapRow[] => {
  const script = 'additionalSales:leadMovingNextories.script.'
  const hasCivility = !!civility && civility !== '-1'
  const hasAddress = addressSelected && !!address.zipcode && !!address.city

  return [
    {
      label: t(`${script}recap_civility`),
      value: hasCivility ? t(`subscriptionForm:customer_module.civility.${civility}`) : '',
      error: hasCivility ? null : t(`${script}civility_error`),
    },
    {
      label: t(`${script}recap_email`),
      value: email ?? '',
      error: isValidEmail(email) ? null : t('additionalSales:pulse.missing.errors.email'),
    },
    {
      label: t(`${script}recap_address`),
      value: addressSelected ? addressLabel(address) : '',
      error: hasAddress ? null : t(`${script}address_error`),
    },
  ]
}
